import { useEffect, useId, useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api } from "@/api/client";
import { InfoHint } from "@dolfin/ui";
import { chartTooltipProps } from "@/components/charts/ChartTooltip";
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { formatDateTime } from "@/lib/format";
import {
  ACTIVE_DEFINITION,
  periodLabel,
  periodLabelLong,
  plural,
  toNumber,
  type ActivityResource,
  type ActivityResponse,
  type Granularity,
} from "./activityResources";
import { ActivityHeatmap } from "./ActivityHeatmap";
import { ActivityOrganisationsTable } from "./ActivityOrganisationsTable";
import { RelativeTime } from "./RelativeTime";
import { ResourceDetailTable } from "./ResourceDetailTable";
import { ResourceTotalsGrid, type ResourceTile } from "./ResourceTotalsGrid";

// Slot 1 of the validated admin palette; --viz-surface follows the card so bar gaps and hatching match it.
const VIZ_STYLES = `
.dolfin-activity { --viz-surface: var(--card); --series-1: #2a78d6; }
.dark .dolfin-activity { --series-1: #3987e5; }
`;

type ChartMode = "bars" | "heatmap";
type ListMode = "active" | "all";

interface ActivityViewProps {
  clientId?: string;
  organisationId?: string;
  /** Show the all-time totals grid. The client page leaves it to its Stats tab. */
  showTotals?: boolean;
}

function fetchActivity(clientId?: string, organisationId?: string) {
  if (organisationId) {
    return api.GET("/admin/organisations/{organisationId}/activity", { params: { path: { organisationId } } });
  }
  if (clientId) return api.GET("/admin/clients/{clientId}/activity", { params: { path: { clientId } } });
  return api.GET("/admin/analytics/activity");
}

/**
 * What organisations created and when: per-period chart (or heatmap), then the breakdown for the selected
 * period. One component for the global page, a client and an organisation; the scope comes from the props.
 */
export function ActivityView({ clientId, organisationId, showTotals = true }: ActivityViewProps) {
  const isOrganisation = !!organisationId;
  const showClient = !clientId && !organisationId;
  const patternId = useId().replace(/:/g, "");

  const [data, setData] = useState<ActivityResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [granularity, setGranularity] = useState<Granularity>("months");
  const [chartMode, setChartMode] = useState<ChartMode>("bars");
  const [listMode, setListMode] = useState<ListMode>("active");
  const [selected, setSelected] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchActivity(clientId, organisationId).then(({ data: body, error: apiError }) => {
      if (cancelled) return;
      if (apiError || !body) setError("The activity figures could not be loaded.");
      else setData(body);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [clientId, organisationId]);

  const periods = useMemo(() => (granularity === "months" ? data?.months : data?.weeks) ?? [], [data, granularity]);

  // Land on the last complete period with activity (what gets invoiced); the in-progress one only as a fallback.
  useEffect(() => {
    const newestFirst = [...periods].reverse();
    const hasActivity = (p: (typeof periods)[number]) => toNumber(p.total) > 0;
    const pick = newestFirst.slice(1).find(hasActivity) ?? newestFirst.find(hasActivity) ?? newestFirst[0];
    setSelected(pick?.start ?? null);
    setExpanded(null);
  }, [periods]);

  const points = useMemo(
    () =>
      periods.map((p, i) => ({
        start: p.start,
        label: periodLabel(p.start, granularity),
        value: toNumber(isOrganisation ? p.total : p.activeOrganisations),
        records: toNumber(p.total),
        partial: i === periods.length - 1,
      })),
    [periods, granularity, isOrganisation],
  );

  const totals = useMemo(() => {
    const tiles: Partial<Record<ActivityResource, ResourceTile>> = {};
    for (const t of data?.totals ?? []) tiles[t.resource] = { count: toNumber(t.count), lastCreatedAt: t.lastCreatedAt ?? null };
    return tiles;
  }, [data]);

  const organisations = data?.organisations ?? [];
  const listed = useMemo(() => {
    if (listMode === "all" || !selected) return organisations;
    return organisations.filter((o) =>
      (granularity === "months" ? o.months : o.weeks).some((p) => p.start === selected && toNumber(p.total) > 0),
    );
  }, [organisations, listMode, selected, granularity]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="size-5" />
      </div>
    );
  }
  if (error || !data) {
    return <p className="text-destructive text-sm">{error ?? "No figures available."}</p>;
  }

  const selectedPeriod = periods.find((p) => p.start === selected);
  const selectedLabel = selected ? periodLabelLong(selected, granularity) : "";
  const unit = granularity === "months" ? "month" : "week";
  const everActive = points.some((p) => p.records > 0);
  const organisation = isOrganisation ? organisations[0] : undefined;

  return (
    <div className="dolfin-activity space-y-4">
      <style>{VIZ_STYLES}</style>

      {isOrganisation && (
        <dl className="text-muted-foreground flex flex-wrap gap-x-6 gap-y-1 text-sm">
          <div className="flex gap-1.5">
            <dt>Last active</dt>
            <dd className="text-foreground font-medium">
              <RelativeTime value={organisation?.lastActiveAt} />
            </dd>
          </div>
          <div className="flex gap-1.5">
            <dt>First activity</dt>
            <dd className="text-foreground font-medium">
              <RelativeTime value={organisation?.firstActivityAt} />
            </dd>
          </div>
          <div className="flex gap-1.5">
            <dt>Records created</dt>
            <dd className="text-foreground font-medium tabular-nums">
              {toNumber(organisation?.total).toLocaleString("en-GB")}
            </dd>
          </div>
        </dl>
      )}

      {showTotals && (
        <Card>
          <CardHeader>
            <CardTitle>All-time records</CardTitle>
            <CardDescription>Everything created to date, with when each kind was last created.</CardDescription>
          </CardHeader>
          <CardContent>
            <ResourceTotalsGrid totals={totals} />
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-1.5">
            {isOrganisation ? "Records created" : "Active organisations"}
            {!isOrganisation && <InfoHint label="What counts as active">{ACTIVE_DEFINITION}</InfoHint>}
          </CardTitle>
          <CardDescription>
            As of {formatDateTime(data.asOf)} · refreshed at most hourly
          </CardDescription>
          <CardAction className="flex flex-wrap items-center gap-2">
            {!isOrganisation && (
              <ToggleGroup
                type="single"
                variant="outline"
                size="sm"
                value={chartMode}
                onValueChange={(v) => v && setChartMode(v as ChartMode)}
                aria-label="Chart style"
              >
                <ToggleGroupItem value="bars">Bars</ToggleGroupItem>
                <ToggleGroupItem value="heatmap">Heatmap</ToggleGroupItem>
              </ToggleGroup>
            )}
            <ToggleGroup
              type="single"
              variant="outline"
              size="sm"
              value={granularity}
              onValueChange={(v) => v && setGranularity(v as Granularity)}
              aria-label="Time range"
            >
              <ToggleGroupItem value="months">12 months</ToggleGroupItem>
              <ToggleGroupItem value="weeks">3 months by week</ToggleGroupItem>
            </ToggleGroup>
          </CardAction>
        </CardHeader>
        <CardContent>
          {!everActive ? (
            <p className="text-muted-foreground py-8 text-center text-sm">
              {isOrganisation
                ? `This organisation hasn't created anything in the last ${granularity === "months" ? "twelve months" : "thirteen weeks"}.`
                : `No organisation created anything in the last ${granularity === "months" ? "twelve months" : "thirteen weeks"}.`}
            </p>
          ) : chartMode === "heatmap" && !isOrganisation ? (
            <ActivityHeatmap
              periods={periods}
              organisations={organisations}
              granularity={granularity}
              selected={selected}
              showClient={showClient}
              onSelect={(start, orgId) => {
                setSelected(start);
                if (orgId) setExpanded(orgId);
              }}
            />
          ) : (
            <>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={points}
                    margin={{ top: 4, right: 4, bottom: 0, left: -16 }}
                    barCategoryGap="20%"
                    onClick={(state) => {
                      const point = points.find((p) => p.label === state?.activeLabel);
                      if (point) setSelected(point.start);
                    }}
                  >
                    <defs>
                      {/* Same hue, hatched in the surface colour: visibly unfinished without a tint that reads grey. */}
                      <pattern id={patternId} patternUnits="userSpaceOnUse" width={6} height={6} patternTransform="rotate(45)">
                        <rect width={6} height={6} fill="var(--series-1)" />
                        <line x1={0} y1={0} x2={0} y2={6} stroke="var(--viz-surface)" strokeWidth={3} />
                      </pattern>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border)" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="var(--color-muted-foreground)" />
                    <YAxis tick={{ fontSize: 11 }} stroke="var(--color-muted-foreground)" allowDecimals={false} />
                    <Tooltip
                      {...chartTooltipProps}
                      formatter={(value, _name, entry) => {
                        const point = entry?.payload as (typeof points)[number] | undefined;
                        const soFar = point?.partial ? " so far" : "";
                        return isOrganisation
                          ? [`${Number(value).toLocaleString("en-GB")}${soFar}`, "Records"]
                          : [`${value}${soFar} · ${plural(point?.records ?? 0, "record")}`, "Active"];
                      }}
                      labelFormatter={(label) => {
                        const point = points.find((p) => p.label === label);
                        return point ? periodLabelLong(point.start, granularity) : String(label);
                      }}
                    />
                    <Bar
                      dataKey="value"
                      name={isOrganisation ? "Records" : "Active"}
                      fill="var(--series-1)"
                      radius={[4, 4, 0, 0]}
                      stroke="var(--viz-surface)"
                      strokeWidth={2}
                      className="cursor-pointer"
                    >
                      {points.map((point) => (
                        <Cell
                          key={point.start}
                          fill={point.partial ? `url(#${patternId})` : "var(--series-1)"}
                          fillOpacity={point.start === selected ? 1 : 0.45}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <p className="text-muted-foreground mt-3 text-xs">
                The hatched {unit} is still in progress. Select a {unit} to see its breakdown - the selected bar is
                the solid one.
              </p>
            </>
          )}
        </CardContent>
      </Card>

      {selected && everActive && (
        <Card>
          <CardHeader>
            <CardTitle>
              {selectedLabel}
              {!isOrganisation && selectedPeriod && (
                <span className="text-muted-foreground font-normal">
                  {" "}
                  · {toNumber(selectedPeriod.activeOrganisations)} active ·{" "}
                  {plural(toNumber(selectedPeriod.total), "record")}
                </span>
              )}
            </CardTitle>
            {!isOrganisation && (
              <CardAction>
                <ToggleGroup
                  type="single"
                  variant="outline"
                  size="sm"
                  value={listMode}
                  onValueChange={(v) => v && setListMode(v as ListMode)}
                  aria-label="Organisations to list"
                >
                  <ToggleGroupItem value="active">Active in {unit}</ToggleGroupItem>
                  <ToggleGroupItem value="all">All organisations</ToggleGroupItem>
                </ToggleGroup>
              </CardAction>
            )}
          </CardHeader>
          <CardContent>
            {isOrganisation ? (
              organisation ? (
                <ResourceDetailTable
                  allTime={organisation.resources}
                  period={(granularity === "months" ? organisation.months : organisation.weeks).find(
                    (p) => p.start === selected,
                  )}
                  periodLabel={periodLabel(selected, granularity)}
                />
              ) : (
                <p className="text-muted-foreground py-4 text-center text-sm">Nothing created yet.</p>
              )
            ) : (
              <ActivityOrganisationsTable
                organisations={listed}
                periodStart={selected}
                periodLabel={periodLabel(selected, granularity)}
                granularity={granularity}
                showClient={showClient}
                expanded={expanded}
                onExpand={setExpanded}
              />
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
